import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import withWidth, { WithWidth } from "@material-ui/core/withWidth";
import debounce from "lodash/debounce";

import { CkEditorHelper, CkEditorSelection, SlideMap } from "./ckeditor/CkEditorHelper";
import { UserAgent } from "../../../utilities/UserAgent";
import { SlideAction } from "./SlideAction";
import FormatBubble, { FormatBubbleSettings } from "./FormatBubble";
import { EventTracking } from "../../../utilities/Tracking/EventTracking";
import EditorActionAlert from "./EditorActionAlert";
import { EditorContext as ReactEditorContext } from "./useEditor";
import { EditorHelper } from "./EditorHelper";
import { Editor } from "./ckeditor/Editor";
import { useBuilder } from "../useBuilder";
import { BuilderAsset } from "../../../entities/BuilderAsset";
import { BuilderAssetService } from "../../../services/BuilderAssetService";
import { ServerResponse } from "../../../services/server/OldWebClient";
import SermonAnalyzer, { AnalyzerResult } from "../../../utilities/sermonAnalyzer/SermonAnalyzer";
import { EditorSlide } from "./EditorSlide";
import { PreviewSlide } from "./PreviewSlide";
import { AssetWritingLogService, WritingLogRequest } from "../../../services/AssetWritingLogService";
import TextToSpeechContext from "./texttospeech/TextToSpeechContext";
import WordCountParser from "../../../utilities/WordCountParser";
import { useOfflineAssets } from "../../offline/useOfflineAssets";
import DateHelper from "../../../utilities/DateHelper";

const saveEditorContentAfterNoActionForMilliseconds = 3000;
const throttleForHeavyProcessingOfEditorChangesInMilliseconds = 3000;
const throttleForHeavyProcessingOfDOMChangesInMilliseconds = 1000;

interface Props extends WithWidth {}

function EditorContext(props: React.PropsWithChildren<Props>) {
	const builder = useBuilder();
	const { preferences, builderAsset, refreshWritingLog } = builder;
	const { onlineMode, saveBuilderAssetOffline } = useOfflineAssets();

	const [iOS] = useState(UserAgent.iOS());

	const [editor, setEditor] = useState<Editor | undefined>(undefined);
	const [, setEditorSlides] = useState<EditorSlide[]>([]);
	const [previewSlides, setPreviewSlides] = useState<PreviewSlide[]>([]);
	const [offlineEnabled, ] = useState<boolean>(builderAsset.offlineMode);

	const [analyzerResults, setAnalyzerResults] = useState<AnalyzerResult>({
		scriptureReferences: [],
		keywords: [],
		wordCount: 0,
		characterCount: 0,
		improvements: [],
		goodResults: [],
		gradeLevel: "",
	});

	const [lastEdit, setLastEdit] = useState<string>(`Last edit: ${DateHelper.timeSinceDate(builderAsset.dateModified)}`);
	const [logDate] = useState<Date>(new Date());
	const [writingLogTimer, setWritingLogTimer] = useState<number>(1);
	const [writingLogCreated, setWritingLogCreated] = useState<boolean>(false);
	const [writingLogId, setWritingLogId] = useState<string>();

	const [saveInProgress, setSaveInProgress] = useState(false);
	const [formatBubbleSettings, setFormatBubbleSettings] = useState<FormatBubbleSettings | undefined>();

	const [snackbarOpen, setSnackbarOpen] = useState(false);
	const [snackbarMessage, setSnackbarMessage] = useState("");

	// Refs to hold values used in editor callbacks (ONLY USE IN EDITOR CALLBACKS)
	// https://reactjs.org/docs/hooks-faq.html#why-am-i-seeing-stale-props-or-state-inside-my-function
	const editorCallbackRef = useRef({
		builder,
		slides: previewSlides,
		editor,
		writingLogCreated,
		writingLogTimer,
		writingLogId,
		preferences,
		offlineEnabled,
	});

	// Update refs on state updates
	useEffect(() => {
		editorCallbackRef.current.builder = builder;
	}, [builder]);

	useEffect(() => {
		editorCallbackRef.current.slides = previewSlides;
	}, [previewSlides]);

	useEffect(() => {
		editorCallbackRef.current.editor = editor;
	}, [editor]);

	useEffect(() => {
		editorCallbackRef.current.writingLogCreated = writingLogCreated;
	}, [writingLogCreated]);

	useEffect(() => {
		editorCallbackRef.current.writingLogTimer = writingLogTimer;
	}, [writingLogTimer]);

	useEffect(() => {
		editorCallbackRef.current.writingLogId = writingLogId;
	}, [writingLogId]);

	useEffect(() => {
		editorCallbackRef.current.offlineEnabled = builderAsset.offlineMode;
	}, [builderAsset.offlineMode]);

	useEffect(() => {
		editorCallbackRef.current.preferences = preferences;
		if (editor) {
			setAnalyzerResults(SermonAnalyzer.analyze(editor.getData(), preferences));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [preferences]);

	useEffect(() => {
		function onWindowResize() {
			setFormatBubbleSettings(undefined);
		}

		window.addEventListener("resize", onWindowResize);
		return () => window.removeEventListener("resize", onWindowResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
}, [setFormatBubbleSettings]);

	const setEditorInstance = (editor: Editor) => {
		editor.setData(builderAsset.text);
		editor.model.document.selection.on("change:range", () => {
			updateFormatBubbleSettings(CkEditorHelper.getSelections(editor));
		});

		if (!/^((?!chrome|android).)*safari/i.test(navigator.userAgent)) {
			CkEditorHelper.injectCopyPasteButtonsIntoToolbar(copyFromBuilder, pasteIntoBuilder);
		}
		editor.model.document.on("change:data", onEditorContentChanged);
		setEditor(editor);
	};

	// Every function under here must get state from editorCallbackRef

	useEffect(() => {
		setInterval(() => {
			const { writingLogTimer, builder } = editorCallbackRef.current;
			setLastEdit(`Last edit: ${DateHelper.timeSinceDate(builder.builderAsset.dateModified)}`);

			if (document.visibilityState === "visible") {
				setWritingLogTimer(writingLogTimer + 1);
			}
		}, 1000);
	}, []);

	const onEditorContentChanged = () => {
		delayedSaveEditorContentToBuilderAsset();
		debouncedProcessingOfEditorContent();
	};

	const onSaveEditorContentToBuilderAsset = useCallback(async () => {
		const { editor, slides, writingLogCreated, writingLogTimer, writingLogId, offlineEnabled, builder } = editorCallbackRef.current;

		if (editor == null) {
			console.log("No editor");
			return;
		}

		setSaveInProgress(true);
		const assetToSave: BuilderAsset = {
			...builderAsset,
			customSlideBackgroundImagePath: builder.builderAsset.customSlideBackgroundImagePath,
			customSlideBackgroundImageUrl: builder.builderAsset.customSlideBackgroundImageUrl,
			slideExporterAspectRatio: builder.builderAsset.slideExporterAspectRatio,
			slideCount: slides.length,
			text: editor.getData(),
			lastModifiedFormFactor: UserAgent.getFormFactor(),
			lastModifiedOnOperatingSystem: UserAgent.getOperatingSystem(),
			offlineMode: offlineEnabled,
		};

		if (offlineEnabled) {
			const updatedOfflineAsset = saveBuilderAssetOffline(assetToSave);
			if (!onlineMode) {
				builder.setBuilderAsset(updatedOfflineAsset);
			}
		}

		if (onlineMode) {
			// Save writing log
			const logToSave: WritingLogRequest = {
				id: writingLogId ? writingLogId : undefined,
				assetId: builderAsset.id,
				dateCreated: logDate,
				writingTimeInSeconds: writingLogTimer,
			};
			const responses = await Promise.all([
				BuilderAssetService.save(assetToSave),
				writingLogCreated ? AssetWritingLogService.updateAssetWritingLog(logToSave) : AssetWritingLogService.createAssetWritingLog(logToSave),
			]);

			const [saveAssetResult, writingLogResult] = responses;

			if (ServerResponse.isSuccess(saveAssetResult)) {
				builder.setBuilderAsset(saveAssetResult.data);
			} else {
				// TODO: Toast in builder!
			}

			if (ServerResponse.isSuccess(writingLogResult)) {
				if (!writingLogCreated) {
					setWritingLogCreated(true);
					setWritingLogId(writingLogResult.data.id);
				}
				refreshWritingLog();
			}
		}

		setSaveInProgress(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [builderAsset, logDate, builder, refreshWritingLog]);

	const delayedSaveEditorContentToBuilderAsset = useMemo(
		() => debounce(onSaveEditorContentToBuilderAsset, saveEditorContentAfterNoActionForMilliseconds),
		[onSaveEditorContentToBuilderAsset]
	);

	// For intensive computation on the editor state
	const processEditorChanges = useCallback(() => {
		const { editor, preferences } = editorCallbackRef.current;

		if (editor == null) {
			console.log("No editor");
			return;
		}
		// editor.getData() is really expensive..
		// Maybe we can switch to document.querySelector("#editor").innerHTML.replaceAll('<br data-cke-filler="true">', '&nbsp;'); at some point

		setAnalyzerResults(SermonAnalyzer.analyze(editor.getData(), preferences));
	}, [editorCallbackRef]);

	const debouncedProcessingOfEditorContent = useMemo(
		() => debounce(processEditorChanges, throttleForHeavyProcessingOfEditorChangesInMilliseconds),
		[processEditorChanges]
	);

	// For intensive computation on the editor dom changing
	const processDomChanges = useCallback(() => {
		const { editor } = editorCallbackRef.current;
		if (editor == null) {
			return;
		}
		const editorSlides = EditorHelper.getEditorSlides();
		setEditorSlides(editorSlides);
		setPreviewSlides(EditorHelper.getPreviewSlides(editorSlides));
	}, []);

	const throttledProcessingOfEditorDOMContent = useMemo(
		() => debounce(processDomChanges, throttleForHeavyProcessingOfDOMChangesInMilliseconds),
		[processDomChanges]
	);

	const getEditorHtml = () => {
		return editor?.getData() ?? "";
	};

	const getBubbleText = () => {
		const { editor } = editorCallbackRef.current;
		if (editor) {
			let results: CkEditorSelection[] = CkEditorHelper.getSelections(editor);
			let data: string = EditorHelper.getTextFromSelections(results);
			return data;
		} else {
			return "";
		}
	};

	const getBubbleWordCount = () => {
		const { editor } = editorCallbackRef.current;

		if (editor) {
			let results: CkEditorSelection[] = CkEditorHelper.getSelections(editor);
			let data: string = EditorHelper.getTextFromSelections(results);
			return WordCountParser.parse(data);
		} else {
			return 0;
		}
	};

	const onNotifySnackBar = (message: string) => {
		setSnackbarOpen(true);
		setSnackbarMessage(message);
	};

	const updateFormatBubbleSettings = (selections: CkEditorSelection[]) => {
		const isMobile = props.width === "sm" || props.width === "xs";
		const settings = EditorHelper.getFormatBubblePostitionSettings(selections, isMobile, iOS);
		if (!settings) {
			setFormatBubbleSettings(undefined);
		} else if ("isBolded" in settings) {
			setFormatBubbleSettings({ desktop: settings });
		} else {
			setFormatBubbleSettings({ mobile: settings });
		}
	};

	const copyFromBuilder = () => {
		document.execCommand("copy");
		let pasteButton = document.getElementById("pasteButton");
		if (pasteButton) {
			pasteButton.classList.add("pasteActive");
		}
		notifyClipboardCopy("Text");
	};

	const pasteIntoBuilder = async () => {
		let copiedText = await EditorHelper.checkIfClipBoardContainsText();
		let myEditor = editor;
		if (!myEditor) {
			myEditor = editorCallbackRef.current.editor;
		}
		if (myEditor === undefined) {
			onNotifySnackBar(`Error pasting from clipboard, editor instance not found. Please refresh and try again.`);
			return;
		}
		myEditor.model.change((writer) => {
			const text = writer.createText(copiedText);
			myEditor!.model.insertContent(text, myEditor!.model.document.selection);
		});

		let pasteButton = document.getElementById("pasteButton");
		if (pasteButton) {
			pasteButton.classList.remove("pasteActive");
		}
		onNotifySnackBar(`Text pasted from clipboard`);
	};

	const notifyClipboardCopy = (nameOfThingCopied: string) => {
		let pasteButton = document.getElementById("pasteButton");
		if (pasteButton) {
			pasteButton.classList.add("pasteActive");
		}
		onNotifySnackBar(`${nameOfThingCopied} copied to clipboard`);
	};

	const onClickSlidePrompt = (action: SlideAction) => {
		if (!editor) {
			return;
		}
		setFormatBubbleSettings(undefined);
		EditorHelper.executeSlideAction(action, editor);
	};

	const onRemoveSlide = (slide: SlideMap) => {
		if (!editor) {
			return;
		}

		let data = CkEditorHelper.removeSlide(editor.getData(), slide);
		editor.setData(data);
		EventTracking.Builder.onRemoveSlideFromDrawer();
	};

	const onRemovePreviewSlide = (previewSlide: PreviewSlide) => {
		if (!editor) {
			return;
		}

		CkEditorHelper.removePreviewSlide(previewSlide.domSlideIds, editor);
		EventTracking.Builder.onRemoveSlideFromDrawer();
	};

	const excecuteEditorAction = (action: string, value?: string) => {
		if (!editor) {
			return;
		}
		setFormatBubbleSettings(undefined);
		editor.execute(action, value ? { value } : undefined);
	};

	const updateAnalyzerResults = () => {
		if (editor) {
			setAnalyzerResults(SermonAnalyzer.analyze(editor.getData(), preferences));
		}
	};

	const toggleSnackbarOpen = () => setSnackbarOpen(!snackbarOpen);

	return (
		<div id="editor-context" style={{ height: "100%" }}>
			<ReactEditorContext.Provider
				value={{
					lastEdit,
					saveInProgress,
					slides: previewSlides,
					...analyzerResults,
					writingLogCreated,
					updateAnalyzerResults: updateAnalyzerResults,
					getBubbleWordCount: getBubbleWordCount,
					setEditor: setEditorInstance,
					onRemoveSlide,
					onRemovePreviewSlide,
					getHtml: getEditorHtml,
					getBubbleText: getBubbleText,
					hideFormatBubble: () => setFormatBubbleSettings(undefined),
					onEditorDomChange: throttledProcessingOfEditorDOMContent,
				}}
			>
				<TextToSpeechContext>
					{props.children}
					<EditorActionAlert
						editor={editor}
						snackbarOpen={snackbarOpen}
						snackbarMessage={snackbarMessage}
						toggleSnackbarOpen={toggleSnackbarOpen}
					/>
					{formatBubbleSettings && (
						<FormatBubble
							settings={formatBubbleSettings}
							onClickSlidePrompt={onClickSlidePrompt}
							excecuteEditorAction={excecuteEditorAction}
						/>
					)}
				</TextToSpeechContext>
			</ReactEditorContext.Provider>
		</div>
	);
}

export default withWidth()(EditorContext);
